import React from 'react';

/**
 * Bouton du socle mobile.
 *
 * Deux garanties par construction, pas par revue :
 *
 * - **Zone tactile de 44 px** (`min-h-touch`), la taille visuelle restant libre.
 *   #34 a écarté un plancher sur le visible : il aurait rouvert l'arbitrage de
 *   #16 sur un critère qui n'a jamais été le sien, alors que 7 de ses 8
 *   contrôles tiennent entre 30 et 40 px.
 * - **Nom accessible obligatoire** : `libelle` est requis par le typage, donc
 *   `tsc --noEmit` refuse un bouton anonyme. C'est le typage qui remplace ici la
 *   règle de lint (#34) — une règle mécanique n'aurait pas su lire un libellé.
 */
type Variante = 'primaire' | 'secondaire' | 'discret' | 'lien' | 'danger';

type Alignement = 'centre' | 'debut';

const ALIGNEMENTS: Record<Alignement, string> = {
    centre: 'justify-center text-center',
    // `text-left` : un <button> centre son texte par défaut ; un libellé sur deux
    // lignes s'affichait centré sous des voisins alignés à gauche.
    debut: 'justify-start text-left',
};

type Espacement = 'normal' | 'aucun';

const ESPACEMENTS: Record<Espacement, string> = {
    normal: 'px-4',
    aucun: 'px-0',
};

const VARIANTES: Record<Variante, string> = {
    primaire: 'bg-main-color text-white',
    secondaire: 'bg-white text-main-color border border-main-color',
    discret: 'bg-transparent text-text-color-light',
    lien: 'bg-transparent text-main-color underline underline-offset-2',
    // Rouge foncé et non `danger-color` : le blanc sur #FF5252 ne passe pas le contraste AA.
    danger: 'bg-alert-text-color text-white',
};

interface ButtonBaseProps {
    /** Texte visible ET nom accessible : les deux ne divergent jamais. */
    libelle: string;
    /**
     * Icône de tête, purement décorative — `aria-hidden`, jamais annoncée.
     *
     * Le nom accessible reste `libelle` : une icône qui parlerait au lecteur
     * d'écran ferait deux annonces pour un seul contrôle. Ajoutée par #75, dont
     * le registre d'écrans déclare une icône par entrée de panneau.
     */
    icone?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    variante?: Variante;
    disabled?: boolean;
    /** Occupe toute la largeur disponible. */
    pleineLargeur?: boolean;
    /**
     * Alignement du contenu. Une PROP et non une classe passée par `className` :
     * `justify-start` et `justify-center` ont la même spécificité, donc c'est
     * l'ordre dans la feuille Tailwind qui tranche, pas l'ordre dans l'attribut.
     * Passer `justify-start` en `className` ne faisait donc rien — défaut vu à
     * l'écran sur le panneau de #75, invisible au type-check comme aux tests.
     */
    alignement?: Alignement;
    /**
     * Rembourrage horizontal. Une PROP pour la même raison que `alignement` :
     * `px-0` passé en `className` ne fait rien — même spécificité que le `px-4`
     * de base, et c'est l'ordre dans la feuille Tailwind qui tranche. Le défaut
     * s'est vu sur le panneau de #75, où deux boutons se retrouvaient décalés de
     * 16 px par rapport au texte qu'ils suivaient.
     */
    espacement?: Espacement;
    /** État de divulgation pour un bouton qui contrôle un élément caché/visible. */
    ariaExpanded?: boolean;
    className?: string;
}

/**
 * `submit` n'accepte PAS `onPress` : le clic soumet le formulaire, et c'est son
 * `onSubmit` qui agit. Brancher les deux appelait le handler deux fois par clic
 * (deux POST /login, deux jetons). Le typage refuse désormais le mélange.
 */
type ButtonProps = ButtonBaseProps & (
    | { type?: 'button'; onPress: () => void }
    | { type: 'submit'; onPress?: never }
);

export default function Button({
    libelle,
    icone: Icone,
    onPress,
    variante = 'primaire',
    disabled = false,
    type = 'button',
    pleineLargeur = false,
    alignement = 'centre',
    espacement = 'normal',
    ariaExpanded,
    className = '',
}: ButtonProps) {
    return (
        // mobile-check-ignore — le socle EST l'endroit où <button> a le droit d'exister ;
        // c'est même son objet : ailleurs, la règle de #34 renvoie ici.
        <button
            type={type}
            onClick={type === 'submit' ? undefined : onPress}
            disabled={disabled}
            aria-expanded={ariaExpanded}
            className={[
                'inline-flex min-h-touch items-center rounded-xl text-base font-semibold',
                'disabled:opacity-50',
                ALIGNEMENTS[alignement],
                ESPACEMENTS[espacement],
                VARIANTES[variante],
                pleineLargeur ? 'w-full' : '',
                className,
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {Icone ? <Icone className="mr-3 h-5 w-5 shrink-0" aria-hidden="true" /> : null}
            {libelle}
        </button>
    );
}
